/**
 * FleetOrderGhostCard Component
 *
 * A "ghost" order card shown at the top of the FleetHub list while a freshly
 * booked inspection syncs from HubSpot → Firestore. Forks the real card chrome
 * (borderRadius 14, Dusknight shadow, mobile-responsive) and renders the known
 * booking draft as real data (date, pickup time, plate, company) while
 * shimmering the server-assigned fields (Prüfstelle, order#, progress, return
 * ETA, actions). On `synced` the shimmer freezes to static grey and the caption
 * becomes a <SyncedCheckPulse/>. Dismiss X shows only during `pending`.
 *
 * @module components/FleetOrderGhostCard
 */

import { useEffect, useState } from "react"
import { Dimensions, TouchableOpacity, View, ViewStyle, TextStyle } from "react-native"
import Animated, { FadeIn, FadeOut } from "react-native-reanimated"
import Icon from "react-native-vector-icons/MaterialIcons"

import type { PendingOrderDraft } from "@/stores/useFleetHubStore"
import { colors, spacing } from "@/theme"

import { NumberPlateVisual } from "./NumberPlateVisual"
import { SkeletonShimmer } from "./SkeletonShimmer"
import { SyncedCheckPulse } from "./SyncedCheckPulse"
import { Text } from "./Text"

export interface FleetOrderGhostCardProps {
  /** Known booking data captured at submit time. */
  draft: PendingOrderDraft
  /** "pending" → shimmer active; "synced" → shimmer frozen + check pulse. */
  phase: "pending" | "synced"
  /** Dismiss callback — only wired to the X button during `pending`. */
  onDismiss?: () => void
}

/** Format a YYYY-MM-DD draft date to DD.MM.YYYY without timezone drift. */
function formatDate(dateString?: string): string {
  if (!dateString) return ""
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString)
  if (isoMatch) {
    const [, year, month, day] = isoMatch
    return `${day}.${month}.${year}`
  }
  return dateString
}

/** Whether a number plate should be rendered (skips empty / "ohne" values). */
function shouldShowNumberPlate(numberPlate: string | undefined): boolean {
  if (!numberPlate) return false
  const normalized = numberPlate.toLowerCase().trim()
  return normalized !== "ohne" && normalized !== "ohne kennzeichen"
}

/**
 * Placeholder that shimmers while pending and freezes to a static grey block
 * once synced, so the card settles instead of animating forever.
 */
function GhostBlock({
  width,
  height,
  borderRadius = 4,
  frozen,
  style,
}: {
  width?: number | string
  height?: number | string
  borderRadius?: number
  frozen: boolean
  style?: ViewStyle
}) {
  if (frozen) {
    const $frozen = {
      width: width as any,
      height: height as any,
      borderRadius,
      backgroundColor: colors.border,
    }
    return <View style={[$frozen, style]} />
  }
  return <SkeletonShimmer width={width} height={height} borderRadius={borderRadius} style={style} />
}

/**
 * FleetOrderGhostCard Component
 */
export function FleetOrderGhostCard({ draft, phase, onDismiss }: FleetOrderGhostCardProps) {
  // Mobile responsiveness (mirrors FleetOrderRowSkeleton).
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const updateLayout = () => setIsMobile(Dimensions.get("window").width < 768)
    updateLayout()
    const subscription = Dimensions.addEventListener("change", updateLayout)
    return () => subscription?.remove()
  }, [])

  const frozen = phase === "synced"
  const formattedDate = formatDate(draft.abholdatum)
  const isPickupDelivery = draft.serviceType === "pickup_delivery"
  const showPlate = shouldShowNumberPlate(draft.numberPlate)

  return (
    <Animated.View
      entering={FadeIn.duration(250)}
      exiting={FadeOut.duration(250)}
      style={$cardOuter}
    >
      <View style={$cardInner}>
        <View style={isMobile ? $contentContainerMobile : $contentContainer}>
          {/* Left Section: known booking data */}
          <View style={isMobile ? $leftSectionMobile : $leftSection}>
            <View style={$leftContent}>
              {formattedDate ? <Text style={$orderNumber}>{formattedDate}</Text> : null}

              {isPickupDelivery ? (
                <View style={{ gap: spacing.xxs }}>
                  <Text style={$detailText} numberOfLines={1}>
                    ~{draft.spatesteAbholung} Uhr Abholung
                  </Text>
                  {/* Return ETA is assigned server-side → shimmer where the time goes */}
                  <View style={$etaClusterRow}>
                    <Text style={$etaClusterLabel} numberOfLines={1}>
                      Geplante Rückgabe
                    </Text>
                    <GhostBlock width={90} height={20} frozen={frozen} style={$etaShimmer} />
                  </View>
                </View>
              ) : (
                <Text style={$detailText} numberOfLines={1}>
                  ~{draft.spatesteAbholung} Uhr
                </Text>
              )}

              {showPlate && (
                <View style={$plateContainer}>
                  <NumberPlateVisual
                    numberPlate={draft.numberPlate as string}
                    sizeMode="compact"
                    showRedBand={true}
                    plateBackgroundColor={colors.surface}
                    inputBackgroundColor={colors.surface}
                    textColor={colors.palette.neutral900}
                    fontSize={14}
                  />
                </View>
              )}
            </View>
          </View>

          {/* Center Section: status + shimmering unknowns */}
          <View style={$centerSection}>
            {/* Progress bar (unknown → shimmer / frozen) */}
            <GhostBlock width="100%" height={8} frozen={frozen} style={$progressBar} />

            {/* Static phase label — grey dot + "Wird gebucht" */}
            <View style={$phaseRow}>
              <View style={$statusDot} />
              <Text style={$phaseText}>Wird gebucht</Text>
            </View>

            {/* Prüfstelle + order number (assigned server-side → shimmer) */}
            <GhostBlock width={150} height={12} frozen={frozen} />
            <GhostBlock width={120} height={12} frozen={frozen} />

            {/* Company badge (known if provided) */}
            {draft.companyName && (
              <View style={$locationBadgeContainer}>
                <Icon name="business" size={14} color={colors.primary} />
                <Text style={$locationBadge} numberOfLines={1}>
                  {draft.companyName}
                </Text>
              </View>
            )}

            {/* Caption: pending copy → check pulse on synced */}
            {phase === "synced" ? (
              <SyncedCheckPulse style={$captionRow} />
            ) : (
              <Text style={$caption}>Wird bestätigt…</Text>
            )}
          </View>

          {/* Right Section: action buttons (unknown → shimmer / frozen) */}
          <View style={isMobile ? $rightSectionMobile : $rightSection}>
            <GhostBlock
              width={isMobile ? 80 : "100%"}
              height={24}
              borderRadius={6}
              frozen={frozen}
            />
            <GhostBlock
              width={isMobile ? 80 : "100%"}
              height={24}
              borderRadius={6}
              frozen={frozen}
            />
            {!isMobile && <GhostBlock width="100%" height={24} borderRadius={6} frozen={frozen} />}
          </View>
        </View>
      </View>

      {/* Dismiss X — pending only (prevents dismiss-mid-finalize race) */}
      {phase === "pending" && onDismiss && (
        <TouchableOpacity
          onPress={onDismiss}
          style={$dismissButton}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Auftrag ausblenden"
        >
          <Icon name="close" size={20} color={colors.textDim} />
        </TouchableOpacity>
      )}
    </Animated.View>
  )
}

// ============================================================================
// Styles (forked from FleetOrderRowSkeleton / FleetOrderRow COMPACT design)
// ============================================================================

const $cardOuter: ViewStyle = {
  marginBottom: spacing.sm,
  borderRadius: 14,
  backgroundColor: colors.surface,
  // Dusknight-tinted layered shadow — never pure black (DESIGN.md)
  shadowColor: "rgba(17, 30, 44, 0.08)",
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 1,
  shadowRadius: 12,
  elevation: 3,
  borderWidth: 1,
  borderColor: "rgba(17, 30, 44, 0.06)",
  overflow: "hidden",
  position: "relative",
  // @ts-ignore - web layered shadow
  boxShadow: "0 1px 2px rgba(17, 30, 44, 0.04), 0 4px 12px rgba(17, 30, 44, 0.06)",
}

const $cardInner: ViewStyle = {
  backgroundColor: colors.surface,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
}

const $contentContainer: ViewStyle = { flexDirection: "row", gap: spacing.sm }
const $contentContainerMobile: ViewStyle = { flexDirection: "column", gap: spacing.sm }

const $leftSection: ViewStyle = {
  width: 150,
  flexShrink: 0,
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  paddingRight: spacing.sm,
  paddingVertical: spacing.xxs,
}

const $leftSectionMobile: ViewStyle = {
  width: "100%",
  flexShrink: 0,
  justifyContent: "center",
  paddingRight: 10,
  paddingVertical: 6,
}

const $leftContent: ViewStyle = { gap: spacing.xxs, alignItems: "flex-start", width: "100%" }

const $orderNumber: TextStyle = {
  fontSize: 13,
  fontWeight: "500",
  color: colors.text,
  marginBottom: 2,
  letterSpacing: 0,
}

const $detailText: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  lineHeight: 17,
  letterSpacing: 0,
}

const $etaClusterRow: ViewStyle = {
  flexDirection: "column",
  gap: spacing.xxs,
  marginTop: 2,
  paddingTop: spacing.xxs,
  borderTopWidth: 1,
  borderTopColor: "rgba(17, 30, 44, 0.06)",
}

const $etaClusterLabel: TextStyle = {
  fontSize: 10,
  color: colors.textDim,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  fontWeight: "600",
}

const $etaShimmer: ViewStyle = { marginTop: 1 }
const $plateContainer: ViewStyle = { width: 135, height: 32, marginTop: 6 }

const $centerSection: ViewStyle = {
  flex: 1,
  gap: spacing.xs - 2,
  paddingHorizontal: spacing.sm,
  paddingVertical: spacing.xxs,
}

const $progressBar: ViewStyle = { marginVertical: spacing.xs - 2 }
const $phaseRow: ViewStyle = { flexDirection: "row", alignItems: "center", gap: spacing.xs }

// Static grey status dot — the order is not live yet ("Wird gebucht").
const $statusDot: ViewStyle = {
  width: 8,
  height: 8,
  borderRadius: 4,
  backgroundColor: colors.palette.neutral400,
}

const $phaseText: TextStyle = {
  fontSize: 14,
  fontWeight: "600",
  color: colors.textDim,
  letterSpacing: 0,
}

const $locationBadgeContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 4,
  marginTop: 4,
}

const $locationBadge: TextStyle = {
  fontSize: 11,
  fontWeight: "600",
  color: colors.primary,
  letterSpacing: 0,
}

const $caption: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  letterSpacing: 0,
  marginTop: spacing.xxs,
}

const $captionRow: ViewStyle = { marginTop: spacing.xxs }

const $rightSection: ViewStyle = {
  width: 165,
  flexShrink: 0,
  flexDirection: "column",
  gap: spacing.xxs,
  alignItems: "stretch",
  paddingLeft: spacing.sm,
  paddingVertical: spacing.xxs,
}

const $rightSectionMobile: ViewStyle = {
  width: "100%",
  flexShrink: 0,
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 8,
  alignItems: "center",
  paddingVertical: 6,
}

const $dismissButton: ViewStyle = {
  position: "absolute",
  top: spacing.xs,
  right: spacing.xs,
  padding: spacing.xxs,
  zIndex: 2,
  cursor: "pointer" as any,
}
